import { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Chip, CircularProgress, Alert, IconButton,
  Tooltip, SwipeableDrawer, Divider, ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import TrainIcon from "@mui/icons-material/Train";
import MapIcon from "@mui/icons-material/Map";
import ListIcon from "@mui/icons-material/List";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// ─── TTC Line config ──────────────────────────────────────────────────────────
const TTC_LINES = [
  { id: "1", label: "Line 1", name: "Yonge-University", color: "#FFCD00", textColor: "#1a1a1a" },
  { id: "2", label: "Line 2", name: "Bloor-Danforth",   color: "#00A651", textColor: "#ffffff" },
  { id: "4", label: "Line 4", name: "Sheppard",          color: "#B22222", textColor: "#ffffff" },
];

const EFFECT_LABELS = {
  NO_SERVICE:          { label: "No Service",          severity: 3 },
  REDUCED_SERVICE:     { label: "Reduced Service",     severity: 2 },
  SIGNIFICANT_DELAYS:  { label: "Significant Delays",  severity: 2 },
  DETOUR:              { label: "Detour",               severity: 1 },
  MODIFIED_SERVICE:    { label: "Modified Service",     severity: 1 },
  ADDITIONAL_SERVICE:  { label: "Additional Service",   severity: 0 },
  STOP_MOVED:          { label: "Stop Moved",           severity: 1 },
  NO_EFFECT:           { label: "Advisory",             severity: 0 },
  ACCESSIBILITY_ISSUE: { label: "Accessibility",        severity: 1 },
  UNKNOWN_EFFECT:      { label: "Alert",                severity: 1 },
};

const CAUSE_LABELS = {
  ACCIDENT: "Accident", CONSTRUCTION: "Construction", DEMONSTRATION: "Demonstration",
  HOLIDAY: "Holiday", MAINTENANCE: "Maintenance", MEDICAL_EMERGENCY: "Medical Emergency",
  POLICE_ACTIVITY: "Police Activity", STRIKE: "Strike", TECHNICAL_PROBLEM: "Technical Problem",
  WEATHER: "Weather", OTHER_CAUSE: "Other", UNKNOWN_CAUSE: "Unknown",
};

// ─── Station data ─────────────────────────────────────────────────────────────
// GTFS stop IDs mapped to coordinates and line membership.
// Line 1 (Yonge-University U-shape), Line 2 (Bloor-Danforth E-W), Line 4 (Sheppard E-W short)
// Coordinates are approximate centroid of each station entrance.

const STATIONS = {
  // Line 1 — southbound Yonge leg (bottom of U, south to north)
  "FINCH":          { name: "Finch",            lat: 43.7803, lng: -79.4148, lines: ["1"] },
  "NORTH_YORK_CTR": { name: "North York Centre",lat: 43.7672, lng: -79.4128, lines: ["1"] },
  "SHEPPARD_YONGE": { name: "Sheppard-Yonge",   lat: 43.7615, lng: -79.4108, lines: ["1","4"] },
  "YORK_MILLS":     { name: "York Mills",        lat: 43.7455, lng: -79.4080, lines: ["1"] },
  "LAWRENCE":       { name: "Lawrence",           lat: 43.7257, lng: -79.4060, lines: ["1"] },
  "EGLINTON":       { name: "Eglinton",           lat: 43.7070, lng: -79.3980, lines: ["1"] },
  "DAVISVILLE":     { name: "Davisville",         lat: 43.6977, lng: -79.3942, lines: ["1"] },
  "ST_CLAIR":       { name: "St Clair",           lat: 43.6882, lng: -79.3924, lines: ["1"] },
  "SUMMERHILL":     { name: "Summerhill",          lat: 43.6792, lng: -79.3897, lines: ["1"] },
  "ROSEDALE":       { name: "Rosedale",            lat: 43.6750, lng: -79.3874, lines: ["1"] },
  "BLOOR_YONGE":    { name: "Bloor-Yonge",        lat: 43.6713, lng: -79.3862, lines: ["1","2"] },
  "WELLESLEY":      { name: "Wellesley",           lat: 43.6654, lng: -79.3842, lines: ["1"] },
  "COLLEGE":        { name: "College",             lat: 43.6601, lng: -79.3828, lines: ["1"] },
  "TMU":            { name: "TMU",                 lat: 43.6552, lng: -79.3808, lines: ["1"] },
  "QUEEN":          { name: "Queen",               lat: 43.6524, lng: -79.3798, lines: ["1"] },
  "KING":           { name: "King",                lat: 43.6487, lng: -79.3786, lines: ["1"] },
  "UNION":          { name: "Union",               lat: 43.6452, lng: -79.3806, lines: ["1"] },
  "ST_ANDREW":      { name: "St Andrew",           lat: 43.6474, lng: -79.3847, lines: ["1"] },
  "OSGOODE":        { name: "Osgoode",             lat: 43.6502, lng: -79.3875, lines: ["1"] },
  "ST_PATRICK":     { name: "St Patrick",          lat: 43.6543, lng: -79.3905, lines: ["1"] },
  "QUEENS_PARK":    { name: "Queens Park",         lat: 43.6597, lng: -79.3930, lines: ["1"] },
  "MUSEUM":         { name: "Museum",              lat: 43.6674, lng: -79.3944, lines: ["1"] },
  "ST_GEORGE":      { name: "St George",           lat: 43.6681, lng: -79.3993, lines: ["1","2"] },
  "SPADINA_1":      { name: "Spadina",             lat: 43.6674, lng: -79.4044, lines: ["1","2"] },
  "DUPONT":         { name: "Dupont",              lat: 43.6753, lng: -79.4076, lines: ["1"] },
  "ST_CLAIR_W":     { name: "St Clair West",       lat: 43.6839, lng: -79.4122, lines: ["1"] },
  "GLENCAIRN":      { name: "Glencairn",          lat: 43.7056, lng: -79.4380, lines: ["1"] },
  "CEDARVALE":      { name: "Cedarvale",           lat: 43.6979, lng: -79.4333, lines: ["1"] },
  "LAWRENCE_W":     { name: "Lawrence West",       lat: 43.7168, lng: -79.4429, lines: ["1"] },
  "YORKDALE":       { name: "Yorkdale",            lat: 43.7247, lng: -79.4475, lines: ["1"] },
  "WILSON":         { name: "Wilson",              lat: 43.7344, lng: -79.4500, lines: ["1"] },
  "SHEPPARD_W":     { name: "Sheppard West",       lat: 43.7512, lng: -79.4780, lines: ["1"] },
  "DOWNSVIEW_PARK": { name: "Downsview Park",      lat: 43.7615, lng: -79.4780, lines: ["1"] },
  "FINCH_W":        { name: "Finch West",          lat: 43.7647, lng: -79.4914, lines: ["1"] },
  "YORK_U":         { name: "York University",     lat: 43.7742, lng: -79.4997, lines: ["1"] },
  "PIONEER_VILLAGE":{ name: "Pioneer Village",     lat: 43.7769, lng: -79.5092, lines: ["1"] },
  "HIGHWAY_407":    { name: "Highway 407",         lat: 43.7844, lng: -79.5294, lines: ["1"] },
  "VAUGHAN_MC":     { name: "Vaughan MC",          lat: 43.7947, lng: -79.5292, lines: ["1"] },

  // Line 2 — Bloor-Danforth (west to east)
  "KIPLING":        { name: "Kipling",             lat: 43.6367, lng: -79.5358, lines: ["2"] },
  "ISLINGTON":      { name: "Islington",           lat: 43.6444, lng: -79.5239, lines: ["2"] },
  "ROYAL_YORK":     { name: "Royal York",          lat: 43.6481, lng: -79.5124, lines: ["2"] },
  "OLD_MILL":       { name: "Old Mill",            lat: 43.6494, lng: -79.4987, lines: ["2"] },
  "JANE":           { name: "Jane",                lat: 43.6494, lng: -79.4875, lines: ["2"] },
  "RUNNYMEDE":      { name: "Runnymede",           lat: 43.6494, lng: -79.4768, lines: ["2"] },
  "HIGH_PARK":      { name: "High Park",           lat: 43.6533, lng: -79.4662, lines: ["2"] },
  "KEELE":          { name: "Keele",               lat: 43.6554, lng: -79.4580, lines: ["2"] },
  "DUNDAS_W":       { name: "Dundas West",         lat: 43.6565, lng: -79.4518, lines: ["2"] },
  "LANSDOWNE":      { name: "Lansdowne",           lat: 43.6565, lng: -79.4428, lines: ["2"] },
  "DUFFERIN":       { name: "Dufferin",            lat: 43.6572, lng: -79.4340, lines: ["2"] },
  "OSSINGTON":      { name: "Ossington",           lat: 43.6617, lng: -79.4254, lines: ["2"] },
  "CHRISTIE":       { name: "Christie",            lat: 43.6646, lng: -79.4169, lines: ["2"] },
  "BATHURST":       { name: "Bathurst",            lat: 43.6661, lng: -79.4110, lines: ["2"] },
  "SPADINA_2":      { name: "Spadina",             lat: 43.6674, lng: -79.4044, lines: ["2"] },
  "BAY":            { name: "Bay",                 lat: 43.6700, lng: -79.3937, lines: ["2"] },
  "SHERBOURNE":     { name: "Sherbourne",          lat: 43.6733, lng: -79.3760, lines: ["2"] },
  "CASTLE_FRANK":   { name: "Castle Frank",        lat: 43.6754, lng: -79.3639, lines: ["2"] },
  "BROADVIEW":      { name: "Broadview",           lat: 43.67694, lng: -79.35833, lines: ["2"] },
  "CHESTER":        { name: "Chester",              lat: 43.67833, lng: -79.35250, lines: ["2"] },
  "PAPE":           { name: "Pape",                 lat: 43.68000, lng: -79.34503, lines: ["2"] },
  "DONLANDS":       { name: "Donlands",             lat: 43.68111, lng: -79.33778, lines: ["2"] },
  "GREENWOOD":      { name: "Greenwood",            lat: 43.68250, lng: -79.33028, lines: ["2"] },
  "COXWELL":        { name: "Coxwell",              lat: 43.68417, lng: -79.32306, lines: ["2"] },
  "WOODBINE":       { name: "Woodbine",             lat: 43.68639, lng: -79.31250, lines: ["2"] },
  "MAIN_ST":        { name: "Main Street",          lat: 43.68675, lng: -79.30100, lines: ["2"] },
  "VICTORIA_PARK":  { name: "Victoria Park",        lat: 43.69033, lng: -79.28650, lines: ["2"] },
  "WARDEN":         { name: "Warden",               lat: 43.71111, lng: -79.27917, lines: ["2"] },
  "KENNEDY":        { name: "Kennedy",              lat: 43.72616, lng: -79.25817, lines: ["2"] },

  // Line 4 — Sheppard (west to east)
  "SHEPPARD_YONGE_4":{ name: "Sheppard-Yonge",    lat: 43.7615, lng: -79.4108, lines: ["4"] },
  "BAYVIEW":         { name: "Bayview",            lat: 43.7667, lng: -79.3869, lines: ["4"] },
  "BESSARION":       { name: "Bessarion",          lat: 43.7697, lng: -79.3728, lines: ["4"] },
  "LESLIE":          { name: "Leslie",             lat: 43.7725, lng: -79.3591, lines: ["4"] },
  "DON_MILLS":       { name: "Don Mills",          lat: 43.7749, lng: -79.3462, lines: ["4"] },
};

// Station sequences per line (ordered list of station keys for segment matching)
const LINE_STATION_SEQUENCE = {
  "1": [
    // Yonge leg: Finch → Union
    "FINCH","NORTH_YORK_CTR","SHEPPARD_YONGE","YORK_MILLS","LAWRENCE","EGLINTON",
    "DAVISVILLE","ST_CLAIR","SUMMERHILL","ROSEDALE","BLOOR_YONGE","WELLESLEY",
    "COLLEGE","TMU","QUEEN","KING","UNION",
    // University/Spadina leg: Union → Vaughan MC
    "ST_ANDREW","OSGOODE","ST_PATRICK","QUEENS_PARK","MUSEUM","ST_GEORGE",
    "SPADINA_1","DUPONT","ST_CLAIR_W","CEDARVALE","GLENCAIRN","LAWRENCE_W",
    "YORKDALE","WILSON","SHEPPARD_W","DOWNSVIEW_PARK","FINCH_W","YORK_U",
    "PIONEER_VILLAGE","HIGHWAY_407","VAUGHAN_MC",
  ],
  "2": [
    "KIPLING","ISLINGTON","ROYAL_YORK","OLD_MILL","JANE","RUNNYMEDE","HIGH_PARK",
    "KEELE","DUNDAS_W","LANSDOWNE","DUFFERIN","OSSINGTON","CHRISTIE","BATHURST",
    "SPADINA_2","ST_GEORGE","BAY","BLOOR_YONGE","SHERBOURNE","CASTLE_FRANK",
    "BROADVIEW","CHESTER","PAPE","DONLANDS","GREENWOOD","COXWELL","WOODBINE",
    "MAIN_ST","VICTORIA_PARK","WARDEN","KENNEDY",
  ],
  "4": [
    "SHEPPARD_YONGE_4","BAYVIEW","BESSARION","LESLIE","DON_MILLS",
  ],
};

// These are the TTC GTFS stop IDs for each subway station (platform stops)

// ─── Station name → key lookup (for alerts.ttc.ca API) ───────────────────────
const STATION_NAME_TO_KEY = {
  "Finch": "FINCH", "North York Centre": "NORTH_YORK_CTR",
  "Sheppard-Yonge": "SHEPPARD_YONGE", "York Mills": "YORK_MILLS",
  "Lawrence": "LAWRENCE", "Eglinton": "EGLINTON",
  "Davisville": "DAVISVILLE", "St Clair": "ST_CLAIR",
  "Summerhill": "SUMMERHILL", "Rosedale": "ROSEDALE",
  "Bloor-Yonge": "BLOOR_YONGE", "Wellesley": "WELLESLEY",
  "College": "COLLEGE", "Dundas": "TMU", "TMU": "TMU", "Queen": "QUEEN",
  "King": "KING", "Union": "UNION", "St Andrew": "ST_ANDREW",
  "Osgoode": "OSGOODE", "St Patrick": "ST_PATRICK",
  "Queen's Park": "QUEENS_PARK", "Museum": "MUSEUM",
  "St George": "ST_GEORGE", "Spadina": "SPADINA_1",
  "Dupont": "DUPONT", "St Clair West": "ST_CLAIR_W",
  "Eglinton West": "CEDARVALE", "Allen": "CEDARVALE", "Fairbank": "CEDARVALE", "Cedarvale": "CEDARVALE",
  "Lawrence West": "LAWRENCE_W", "Glencairn": "GLENCAIRN",
  "Wilson": "WILSON", "Sheppard West": "SHEPPARD_W",
  "Downsview Park": "DOWNSVIEW_PARK", "Finch West": "FINCH_W",
  "York University": "YORK_U", "Pioneer Village": "PIONEER_VILLAGE",
  "Highway 407": "HIGHWAY_407", "Vaughan Metropolitan Centre": "VAUGHAN_MC", "Vaughan MC": "VAUGHAN_MC",
  "Kipling": "KIPLING", "Islington": "ISLINGTON",
  "Royal York": "ROYAL_YORK", "Old Mill": "OLD_MILL",
  "Jane": "JANE", "Runnymede": "RUNNYMEDE",
  "High Park": "HIGH_PARK", "Keele": "KEELE",
  "Dundas West": "DUNDAS_W", "Lansdowne": "LANSDOWNE",
  "Dufferin": "DUFFERIN", "Ossington": "OSSINGTON",
  "Christie": "CHRISTIE", "Bathurst": "BATHURST",
  "Bay": "BAY", "Sherbourne": "SHERBOURNE",
  "Castle Frank": "CASTLE_FRANK", "Broadview": "BROADVIEW",
  "Chester": "CHESTER", "Pape": "PAPE", "Donlands": "DONLANDS",
  "Greenwood": "GREENWOOD", "Coxwell": "COXWELL",
  "Woodbine": "WOODBINE", "Main Street": "MAIN_ST",
  "Victoria Park": "VICTORIA_PARK", "Warden": "WARDEN",
  "Kennedy": "KENNEDY",
  "Bayview": "BAYVIEW", "Bessarion": "BESSARION",
  "Leslie": "LESLIE", "Don Mills": "DON_MILLS",
};

// ─── Segment helpers ──────────────────────────────────────────────────────────
function getAffectedSegments(alerts) {
  const segments = [];
  for (const alert of alerts) {
    // Don't highlight speed restrictions on map — they're minor and stack confusingly
    if (alert.effect === "SIGNIFICANT_DELAYS") continue;
    const severity = EFFECT_LABELS[alert.effect]?.severity ?? 1;
    const routeId = alert.route;
    const seq = LINE_STATION_SEQUENCE[routeId];
    if (!seq) continue;

    const startKey = STATION_NAME_TO_KEY[alert.stopStart];
    const endKey   = STATION_NAME_TO_KEY[alert.stopEnd];

    if (startKey && endKey && seq.includes(startKey) && seq.includes(endKey)) {
      const i1 = seq.indexOf(startKey);
      const i2 = seq.indexOf(endKey);
      const segKeys = seq.slice(Math.min(i1, i2), Math.max(i1, i2) + 1);
      segments.push({ lineId: routeId, stationKeys: segKeys, isFullLine: false, severity, alertId: alert.id });
    } else if (startKey && seq.includes(startKey)) {
      const idx = seq.indexOf(startKey);
      const segKeys = seq.slice(Math.max(0, idx - 1), idx + 2);
      segments.push({ lineId: routeId, stationKeys: segKeys, isFullLine: false, severity, alertId: alert.id });
    } else {
      segments.push({ lineId: routeId, stationKeys: seq, isFullLine: true, severity, alertId: alert.id });
    }
  }
  return segments;
}

function segmentToLatLngs(stationKeys) {
  return stationKeys.map(k => STATIONS[k]).filter(Boolean).map(s => [s.lat, s.lng]);
}

// eslint-disable-next-line no-unused-vars
function severityColor(effect) {
  const s = EFFECT_LABELS[effect]?.severity ?? 1;
  if (s >= 3) return { bg: "#7f1d1d", text: "#fca5a5", border: "#ef4444" };
  if (s === 2) return { bg: "#431407", text: "#fb923c", border: "#f97316" };
  return { bg: "#1e3a5f", text: "#93c5fd", border: "#3b82f6" };
}

function severityToColor(severity) {
  if (severity >= 3) return "#ef4444";
  if (severity === 2) return "#f97316";
  return "#facc15";
}

async function fetchSubwayAlerts() {
  const res = await fetch("/api/ttc-subway");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);

  const nowSec = Math.floor(Date.now() / 1000);
  return data
    .filter(a => {
      if (a.startTs > 0 && a.startTs > nowSec) return false;
      if (a.endTs > 0 && a.endTs < nowSec) return false;
      if (a.effect === "REDUCED_SERVICE") {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 23) return false;
      }
      if (a.effect === "SIGNIFICANT_DELAYS" && a.endTs === 0 && a.startTs > 0) {
        if (nowSec - a.startTs > 7 * 24 * 60 * 60) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const sa = EFFECT_LABELS[a.effect]?.severity ?? 1;
      const sb = EFFECT_LABELS[b.effect]?.severity ?? 1;
      if (sb !== sa) return sb - sa;
      return a.startTs - b.startTs;
    });
}


async function fetchGOStatus() {
  const res = await fetch("/api/go-status");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return (data?.Trains?.Train || [])
    .filter(t => ["LW", "LE"].includes(t.CorridorCode))
    .map(t => ({
    code: t.CorridorCode,
    name: t.CorridorName,
    color: t.LineColour,
    status: t.NetworkStatus?.RealTimeStatus || "UNKNOWN",
    updates: t.TotalUpdates || 0,
    notifications: (t.Notifications?.Notification || []).map(n => ({
      subject: n.MessageSubject,
      posted: n.PostedDateTime,
    })),
    trains: (t.SaagNotifications?.SaagNotification || []).map(s => ({
      headSign: s.HeadSign,
      delay: s.DelayDuration,
      reason: s.DelayReason,
      status: s.Status,
      trip: s.TripNumbers?.[0],
    })),
  }));
}

// ─── GO & UP Express stations (Toronto-area only) ────────────────────────────
const GO_STATIONS = [
  { code: "DA", name: "Danforth GO",  lat: 43.6866, lng: -79.2994, lines: ["LE"], color: "#ff0d00" },
  { code: "UN", name: "Union Station", lat: 43.6456, lng: -79.3795, lines: ["LE","LW","MI","GT","BR","ST","RH"], color: "#00853e" },
  { code: "EX", name: "Exhibition GO", lat: 43.6359, lng: -79.4192, lines: ["LW"], color: "#98002e" },
];

const UP_STATIONS = [
  { code: "UN", name: "Union Station (UP)", lat: 43.6456, lng: -79.3806, lines: ["UP"], color: "#0072BB" },
  { code: "BL", name: "Bloor Station",      lat: 43.6580, lng: -79.4509, lines: ["UP"], color: "#0072BB" },
  { code: "MD", name: "Mount Dennis",       lat: 43.6875, lng: -79.4872, lines: ["UP"], color: "#0072BB" },
  { code: "WE", name: "Weston Station",     lat: 43.7002, lng: -79.5132, lines: ["UP"], color: "#0072BB" },
  { code: "PA", name: "Pearson Airport",    lat: 43.6777, lng: -79.6248, lines: ["UP"], color: "#0072BB" },
];

function fmtTs(ts) {
  if (!ts) return null;
  return new Date(ts * 1000).toLocaleDateString("en-CA", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}

// ─── Map ──────────────────────────────────────────────────────────────────────
function MapFitBounds() {
  const map = useMap();
  useEffect(() => {
    map.fitBounds([[43.6367, -79.5358], [43.7803, -79.3462]], { padding: [30, 30] });
  }, [map]);
  return null;
}

function SubwayMap({ alerts, colorMode, border, lineFilter, goLines, upAlerts }) {
  const dark = colorMode === "dark";
  const tileUrl = dark
    ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
    : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";

  const affectedSegments = getAffectedSegments(alerts);

  // Build a set of station keys with disruptions for dot coloring
  const disruptedStations = new Set();
  affectedSegments.forEach(seg => seg.stationKeys.forEach(k => disruptedStations.add(k)));

  return (
    <MapContainer
      center={[43.710, -79.420]}
      zoom={11}
      style={{ width: "100%", height: "100%" }}
      zoomControl
    >
      <TileLayer url={tileUrl} attribution='&copy; <a href="https://carto.com/">CARTO</a>' />
      {dark && (
        <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png" opacity={0.85} pane="shadowPane" />
      )}
      <MapFitBounds />

      {/* Draw base line routes */}
      {TTC_LINES.map(line => {
        const seq = LINE_STATION_SEQUENCE[line.id];
        const coords = seq.map(k => STATIONS[k]).filter(Boolean).map(s => [s.lat, s.lng]);
        return (
          <Polyline
            key={`base-${line.id}`}
            positions={coords}
            pathOptions={{ color: line.color, weight: 5, opacity: 0.7 }}
          />
        );
      })}

      {/* Draw disruption segments on top */}
      {affectedSegments.map((seg, i) => {
        const coords = segmentToLatLngs(seg.stationKeys);
        if (coords.length < 2) return null;
        const color = severityToColor(seg.severity);
        return (
          <Polyline
            key={`disruption-${seg.alertId}-${i}`}
            positions={coords}
            pathOptions={{ color, weight: 7, opacity: 0.95, dashArray: seg.isFullLine ? "none" : "none" }}
          />
        );
      })}

      {/* Station dots */}
      {Object.entries(STATIONS).map(([key, station]) => {
        const isDisrupted = disruptedStations.has(key);
        const line = TTC_LINES.find(l => station.lines.includes(l.id));
        const color = isDisrupted ? "#ef4444" : (line?.color || "#ffffff");
        const radius = isDisrupted ? 6 : 4;
        return (
          <CircleMarker
            key={key}
            center={[station.lat, station.lng]}
            radius={radius}
            pathOptions={{ color: "#fff", fillColor: color, fillOpacity: 1, weight: 1.5 }}
          >
            <Popup minWidth={140}>
              <div style={{ fontFamily: "'Outfit', sans-serif", padding: "2px 0" }}>
                <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#0f172a", marginBottom: 3 }}>
                  {station.name}
                </div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {station.lines.map(lid => {
                    const l = TTC_LINES.find(x => x.id === lid);
                    return l ? (
                      <span key={lid} style={{ fontSize: "0.68rem", fontWeight: 700, background: l.color, color: l.textColor, padding: "1px 6px", borderRadius: 4 }}>
                        {l.label}
                      </span>
                    ) : null;
                  })}
                </div>
                {isDisrupted && (
                  <div style={{ marginTop: 5, fontSize: "0.7rem", color: "#ef4444", fontWeight: 700 }}>
                    ⚠ Disruption active
                  </div>
                )}
              </div>
            </Popup>
          </CircleMarker>
        );
      })}

      {/* UP Express route line */}
      <Polyline
        positions={UP_STATIONS.map(s => [s.lat, s.lng])}
        pathOptions={{ color: "#0072BB", weight: 4, opacity: 0.8, dashArray: "8 4" }}
      />

      {/* GO Train line (Lakeshore corridor through Toronto) */}
      <Polyline
        positions={GO_STATIONS.map(s => [s.lat, s.lng])}
        pathOptions={{ color: "#00853F", weight: 4, opacity: 0.8, dashArray: "8 4" }}
      />

      {/* GO Train stations */}
      {GO_STATIONS.map(station => {
        const goLine = goLines?.find(l => station.lines.includes(l.code));
        const hasIssue = goLine && goLine.updates > 0;
        return (
          <CircleMarker
            key={`go-${station.code}`}
            center={[station.lat, station.lng]}
            radius={hasIssue ? 9 : 7}
            pathOptions={{ color: "#fff", fillColor: station.color, fillOpacity: 1, weight: 2 }}
          >
            <Popup autoPan autoPanPadding={[20, 20]} minWidth={200}>
              <div style={{ fontFamily: "'Outfit', sans-serif", padding: "2px 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#00853F", flexShrink: 0 }} />
                  <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#0f172a" }}>{station.name}</div>
                </div>
                <div style={{ fontSize: "0.72rem", color: "#64748b", marginBottom: 5 }}>GO Train</div>
                {hasIssue ? (
                  goLine.notifications.map((n, i) => (
                    <div key={i} style={{ fontSize: "0.75rem", color: "#9a3412", fontWeight: 600, lineHeight: 1.4, marginBottom: 3 }}>
                      ⚠ {n.subject}
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: "0.72rem", color: "#10b981", fontWeight: 600 }}>✓ Good Service</div>
                )}
              </div>
            </Popup>
          </CircleMarker>
        );
      })}

      {/* UP Express line */}
      <Polyline
        positions={UP_STATIONS.map(s => [s.lat, s.lng])}
        pathOptions={{ color: "#0072BB", weight: 4, opacity: 0.8, dashArray: "8 4" }}
      />

      {/* UP Express stations */}
      {UP_STATIONS.map(station => {
        const hasIssue = upAlerts && upAlerts.length > 0;
        return (
          <CircleMarker
            key={`up-${station.code}`}
            center={[station.lat, station.lng]}
            radius={hasIssue ? 9 : 7}
            pathOptions={{ color: "#fff", fillColor: "#0072BB", fillOpacity: 1, weight: 2, dashArray: "none" }}
          >
            <Popup autoPan autoPanPadding={[20, 20]} minWidth={200}>
              <div style={{ fontFamily: "'Outfit', sans-serif", padding: "2px 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#0072BB", flexShrink: 0 }} />
                  <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#0f172a" }}>{station.name}</div>
                </div>
                <div style={{ fontSize: "0.72rem", color: "#64748b", marginBottom: 5 }}>UP Express</div>
                {hasIssue ? (
                  upAlerts.map((a, i) => (
                    <div key={i} style={{ marginBottom: 5 }}>
                      <div style={{ fontSize: "0.75rem", color: "#9a3412", fontWeight: 600, lineHeight: 1.4, marginBottom: 2 }}>
                        ⚠ {a.title}
                      </div>
                      {a.description && (
                        <div style={{ fontSize: "0.72rem", color: "#64748b", lineHeight: 1.5 }}>{a.description}</div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: "0.72rem", color: "#10b981", fontWeight: 600 }}>✓ Good Service</div>
                )}
              </div>
            </Popup>
          </CircleMarker>
        );
      })}

      {/* Map legend */}
      <div style={{
        position: "absolute", bottom: 16, right: 10, zIndex: 1000,
        background: dark ? "rgba(17,24,39,0.93)" : "rgba(255,255,255,0.93)",
        backdropFilter: "blur(8px)", border: `1px solid ${border}`,
        borderRadius: 8, padding: "8px 12px", pointerEvents: "none",
      }}>
        <div style={{ fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b", marginBottom: 5 }}>Lines</div>
        {TTC_LINES.map(l => (
          <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
            <div style={{ width: 20, height: 4, borderRadius: 2, background: l.color }} />
            <span style={{ fontSize: "0.72rem", color: dark ? "#94a3b8" : "#64748b" }}>{l.label} · {l.name}</span>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <div style={{ width: 20, height: 4, borderRadius: 2, background: "#00853F", opacity: 0.85, backgroundImage: "repeating-linear-gradient(90deg,#00853F 0,#00853F 8px,transparent 8px,transparent 12px)" }} />
          <span style={{ fontSize: "0.72rem", color: dark ? "#94a3b8" : "#64748b" }}>GO Train</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <div style={{ width: 20, height: 4, borderRadius: 2, background: "#0072BB", opacity: 0.85, backgroundImage: "repeating-linear-gradient(90deg,#0072BB 0,#0072BB 8px,transparent 8px,transparent 12px)" }} />
          <span style={{ fontSize: "0.72rem", color: dark ? "#94a3b8" : "#64748b" }}>UP Express</span>
        </div>
        <div style={{ marginTop: 6, borderTop: `1px solid ${border}`, paddingTop: 5 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
            <div style={{ width: 20, height: 4, borderRadius: 2, background: "#ef4444" }} />
            <span style={{ fontSize: "0.72rem", color: dark ? "#94a3b8" : "#64748b" }}>No service</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
            <div style={{ width: 20, height: 4, borderRadius: 2, background: "#f97316" }} />
            <span style={{ fontSize: "0.72rem", color: dark ? "#94a3b8" : "#64748b" }}>Major delay</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 20, height: 4, borderRadius: 2, background: "#facc15" }} />
            <span style={{ fontSize: "0.72rem", color: dark ? "#94a3b8" : "#64748b" }}>Minor alert</span>
          </div>
        </div>
      </div>
    </MapContainer>
  );
}

// ─── Alert card ───────────────────────────────────────────────────────────────

// ─── TTC Status Section ───────────────────────────────────────────────────────
function TTCStatusSection({ alerts, dark, border }) {
  const [expandedLine, setExpandedLine] = useState(null);

  return (
    <Box sx={{ borderBottom: `1px solid ${border}` }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 1.25, pb: 0.75 }}>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary" }}>TTC Subway Status</Typography>
        <Box component="a" href="https://www.ttc.ca" target="_blank" rel="noreferrer" sx={{ display: "flex", alignItems: "center", gap: 0.4, fontSize: "0.7rem", color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", textDecoration: "none", "&:hover": { opacity: 0.7 } }}>TTC<OpenInNewIcon sx={{ fontSize: 11 }} /></Box>
      </Box>
      {TTC_LINES.map(line => {
        const lineAlerts = alerts.filter(a => a.route === line.id);
        const maxSev = lineAlerts.reduce((m, a) => Math.max(m, EFFECT_LABELS[a.effect]?.severity ?? 0), 0);
        const isOk = lineAlerts.length === 0;
        const isExp = expandedLine === line.id;

        return (
          <Box key={line.id}
            onClick={() => !isOk && setExpandedLine(isExp ? null : line.id)}
            sx={{
              px: 2, py: 0.75,
              borderTop: `1px solid ${dark ? "#1e293b" : "#f1f5f9"}`,
              cursor: !isOk ? "pointer" : "default",
              "&:hover": !isOk ? { bgcolor: dark ? "#1e293b33" : "#f8fafc" } : {},
            }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: line.color, flexShrink: 0, border: `1px solid ${dark ? "#ffffff22" : "#00000022"}` }} />
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "text.primary" }}>
                {line.label}
              </Typography>
              <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", flex: 1 }}>
                {line.name}
              </Typography>
              {isOk ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 13, color: "#10b981" }} />
                  <Typography sx={{ fontSize: "0.72rem", color: "#10b981", fontWeight: 600 }}>Good Service</Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {maxSev >= 3
                    ? <ErrorOutlineIcon sx={{ fontSize: 13, color: "#fca5a5" }} />
                    : maxSev === 2
                      ? <WarningAmberIcon sx={{ fontSize: 13, color: "#fb923c" }} />
                      : <InfoOutlinedIcon sx={{ fontSize: 13, color: "#93c5fd" }} />}
                  <Chip
                    label={`${lineAlerts.length} alert${lineAlerts.length !== 1 ? "s" : ""}`}
                    size="small"
                    sx={{
                      height: 16, fontSize: "0.62rem", fontWeight: 700,
                      bgcolor: maxSev >= 3 ? (dark ? "#7f1d1d" : "#fef2f2") : maxSev === 2 ? (dark ? "#431407" : "#fff7ed") : (dark ? "#1e3a5f" : "#eff6ff"),
                      color: maxSev >= 3 ? "#fca5a5" : maxSev === 2 ? "#fb923c" : "#93c5fd",
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Expanded alerts for this line */}
            {isExp && lineAlerts.map((alert, i) => {
              const colors = severityColor(alert.effect);
              const effectInfo = EFFECT_LABELS[alert.effect] || { label: "Alert" };
              return (
                <Box key={i} sx={{ mt: 0.75, pl: "22px", mb: i < lineAlerts.length - 1 ? 1 : 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                    <Chip label={effectInfo.label} size="small" sx={{ height: 22, fontSize: "0.8rem", fontWeight: 700, bgcolor: dark ? colors.bg : "#f1f5f9", color: dark ? colors.text : "#475569" }} />
                    {alert.cause && CAUSE_LABELS[alert.cause] && CAUSE_LABELS[alert.cause] !== "Unknown" && (
                      <Chip label={CAUSE_LABELS[alert.cause]} size="small" sx={{ height: 22, fontSize: "0.8rem", bgcolor: "transparent", color: "text.secondary", border: `1px solid ${dark ? "#1e293b" : "#e2e8f0"}` }} />
                    )}
                    {alert.startTs > 0 && (
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: dark ? colors.text : "#374151" }}>
                        {fmtTs(alert.startTs)}{alert.endTs > 0 ? ` → ${fmtTs(alert.endTs)}` : " (ongoing)"}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: "0.85rem", color: dark ? colors.text : "#0f172a", fontWeight: 600, lineHeight: 1.4, mb: 0.25 }}>
                    {alert.headerText}
                  </Typography>
                  {alert.direction && alert.direction !== "Both Ways" && (
                    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.25 }}>
                      {alert.direction}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
}

async function fetchUPStatus() {
  const res = await fetch("/api/up-status");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return await res.json();
}

// ─── UP Express Status Section ────────────────────────────────────────────────
const UP_BLUE = "#0072BB";

function UPStatusSection({ dark, border }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchUPStatus().then(setData).catch(() => {}).finally(() => setLoading(false));
    const t = setInterval(() => fetchUPStatus().then(setData).catch(() => {}), 120000);
    return () => clearInterval(t);
  }, []);

  const alerts = [...(data?.trainAlerts || []), ...(data?.stationAlerts || [])];
  const hasAlerts = alerts.length > 0;

  return (
    <Box sx={{ borderBottom: `1px solid ${border}` }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 1.25, pb: 0.75 }}>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary" }}>UP Express Status</Typography>
        <Box component="a" href="https://www.upexpress.com/en/service-alerts" target="_blank" rel="noreferrer" sx={{ display: "flex", alignItems: "center", gap: 0.4, fontSize: "0.7rem", color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", textDecoration: "none", "&:hover": { opacity: 0.7 } }}>UP Express<OpenInNewIcon sx={{ fontSize: 11 }} /></Box>
      </Box>
      <Box onClick={() => hasAlerts && setExpanded(e => !e)}
        sx={{
          px: 2, py: 0.75,
          borderTop: `1px solid ${dark ? "#1e293b" : "#f1f5f9"}`,
          cursor: hasAlerts ? "pointer" : "default",
          "&:hover": hasAlerts ? { bgcolor: dark ? "#1e293b33" : "#f8fafc" } : {},
        }}>
        {loading ? <CircularProgress size={12} /> : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: UP_BLUE, flexShrink: 0, border: `1px solid ${dark ? "#ffffff22" : "#00000022"}` }} />
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "text.primary", flex: 1 }}>
              Union Pearson Express
            </Typography>
            {hasAlerts ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <WarningAmberIcon sx={{ fontSize: 13, color: "#fb923c" }} />
                <Chip label={`${alerts.length} update${alerts.length !== 1 ? "s" : ""}`} size="small"
                  sx={{ height: 16, fontSize: "0.62rem", fontWeight: 700, bgcolor: dark ? "#431407" : "#fff7ed", color: "#fb923c" }} />
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 13, color: "#10b981" }} />
                <Typography sx={{ fontSize: "0.72rem", color: "#10b981", fontWeight: 600 }}>Good Service</Typography>
              </Box>
            )}
          </Box>
        )}
        {expanded && hasAlerts && alerts.map((alert, i) => (
          <Box key={i} sx={{ mt: 0.75, pl: "22px", mb: 1 }}>
            <Typography sx={{ fontSize: "0.75rem", color: dark ? "#fb923c" : "#9a3412", fontWeight: 700, lineHeight: 1.4, mb: 0.5 }}>
              {alert.title}
            </Typography>
            {alert.description && (
              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", lineHeight: 1.6, mb: 0.5 }}>
                {alert.description}
              </Typography>
            )}
            {alert.posted && (
              <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", opacity: 0.6 }}>
                Posted {new Date(alert.posted).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ─── GO Status Section ────────────────────────────────────────────────────────
function GOStatusSection({ dark, border }) {
  const [goLines, setGoLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    fetchGOStatus()
      .then(setGoLines)
      .catch(() => {})
      .finally(() => setLoading(false));
    const t = setInterval(() => fetchGOStatus().then(setGoLines).catch(() => {}), 60000);
    return () => clearInterval(t);
  }, []);

  if (loading) return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${border}` }}>
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", mb: 1 }}>GO Train Status</Typography>
      <CircularProgress size={14} />
    </Box>
  );

  return (
    <Box sx={{ borderBottom: `1px solid ${border}` }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, pt: 1.25, pb: 0.75 }}>
        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary" }}>GO Train Status</Typography>
        <Box component="a" href="https://www.gotransit.com/en" target="_blank" rel="noreferrer" sx={{ display: "flex", alignItems: "center", gap: 0.4, fontSize: "0.7rem", color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", textDecoration: "none", "&:hover": { opacity: 0.7 } }}>GO Train<OpenInNewIcon sx={{ fontSize: 11 }} /></Box>
      </Box>
      {goLines.map(line => {
        const isGood = line.status === "GOODSERVICE";
        const isExp = expanded === line.code;
        return (
          <Box key={line.code} onClick={() => line.updates > 0 && setExpanded(isExp ? null : line.code)}
            sx={{
              px: 2, py: 0.75,
              borderTop: `1px solid ${dark ? "#1e293b" : "#f1f5f9"}`,
              cursor: line.updates > 0 ? "pointer" : "default",
              "&:hover": line.updates > 0 ? { bgcolor: dark ? "#1e293b33" : "#f8fafc" } : {},
            }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {/* Line colour dot */}
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: line.color, flexShrink: 0, border: `1px solid ${dark ? "#ffffff22" : "#00000022"}` }} />
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "text.primary", flex: 1 }}>
                {line.name}
              </Typography>
              {isGood ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 13, color: "#10b981" }} />
                  <Typography sx={{ fontSize: "0.72rem", color: "#10b981", fontWeight: 600 }}>Good Service</Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <WarningAmberIcon sx={{ fontSize: 13, color: "#fb923c" }} />
                  <Chip label={`${line.updates} update${line.updates !== 1 ? "s" : ""}`} size="small"
                    sx={{ height: 16, fontSize: "0.62rem", fontWeight: 700, bgcolor: dark ? "#431407" : "#fff7ed", color: "#fb923c" }} />
                </Box>
              )}
            </Box>
            {/* Expanded notifications */}
            {isExp && line.notifications.map((n, i) => (
              <Box key={i} sx={{ mt: 0.75, pl: "22px" }}>
                <Typography sx={{ fontSize: "0.75rem", color: dark ? "#fb923c" : "#9a3412", fontWeight: 600, lineHeight: 1.4 }}>
                  {n.subject}
                </Typography>
                {line.trains.map((tr, j) => (
                  <Typography key={j} sx={{ fontSize: "0.72rem", color: "text.secondary", mt: 0.25 }}>
                    Train {tr.trip} · {tr.reason} · {tr.status}
                  </Typography>
                ))}
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── TransitPage ──────────────────────────────────────────────────────────────
export default function TransitPage({ colorMode, setColorMode, dark, border, barBg, isMobile }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [lineFilter, setLineFilter] = useState("all");
  const [viewMode, setViewMode] = useState("list");
  const [goLines, setGoLines] = useState([]);
  const [upAlerts, setUpAlerts] = useState([]);

  const load = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const data = await fetchSubwayAlerts();
      setAlerts(data);
      setLastRefresh(new Date());
    } catch (e) {
      setError(`Failed to load TTC alerts: ${e.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => load(), 60000);
    // Also fetch GO and UP status for map markers
    fetchGOStatus().then(setGoLines).catch(() => {});
    fetchUPStatus().then(d => setUpAlerts([...(d?.trainAlerts||[]), ...(d?.stationAlerts||[])])).catch(() => {});
    return () => clearInterval(interval);
  }, [load]);

  const displayed = lineFilter === "all"
    ? alerts
    : alerts.filter(a => a.route === lineFilter);

  const activeFilterCount = [lineFilter !== "all"].filter(Boolean).length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* Header */}
      <Box sx={{ height: 52, display: "flex", alignItems: "center", px: 2, gap: 1, borderBottom: `1px solid ${border}`, bgcolor: barBg, flexShrink: 0 }}>
        <TrainIcon sx={{ fontSize: 16, color: "#FFCD00" }} />
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>Transit Disruptions</Typography>
        {!isMobile && (
          <Chip label={viewMode === "map" ? "TTC · Map" : "TTC · Live"} size="small"
            sx={{ fontSize: "0.65rem", height: 18, bgcolor: dark ? "#1e293b" : "#f1f5f9", color: "text.secondary", fontWeight: 600, ml: 0.5 }} />
        )}
        <Box sx={{ flex: 1 }} />
        {alerts.length > 0 && <Chip label={`${alerts.length} alert${alerts.length !== 1 ? "s" : ""}`} size="small" sx={{ fontSize: "0.7rem", height: 20, bgcolor: dark ? "#1e293b" : "#f1f5f9", color: "text.secondary", fontWeight: 700 }} />}
        {lastRefresh && <Typography variant="caption" sx={{ color: "text.secondary", display: { xs: "none", sm: "block" }, fontSize: "0.72rem" }}>{lastRefresh.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", hour12: false })}</Typography>}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => load(true)} disabled={refreshing} sx={{ color: "text.secondary" }}>
            {refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Filters">
          <IconButton size="small" onClick={() => setFilterDrawerOpen(true)} sx={{ color: activeFilterCount > 0 ? "#3b82f6" : "text.secondary" }}>
            <TuneIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filter Drawer */}
      <SwipeableDrawer
        anchor={isMobile ? "bottom" : "right"}
        open={filterDrawerOpen}
        onOpen={() => setFilterDrawerOpen(true)}
        onClose={() => setFilterDrawerOpen(false)}
        disableSwipeToOpen
        PaperProps={{ sx: { bgcolor: barBg, borderTop: isMobile ? `1px solid ${border}` : "none", borderLeft: !isMobile ? `1px solid ${border}` : "none", borderRadius: isMobile ? "12px 12px 0 0" : 0, width: isMobile ? "100%" : 280, maxHeight: isMobile ? "80vh" : "100vh" } }}
      >
        <Box sx={{ px: 2.5, pt: isMobile ? 2 : 2.5, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${border}` }}>
          {isMobile && <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: border, position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)" }} />}
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>Filters</Typography>
          <IconButton size="small" onClick={() => setFilterDrawerOpen(false)} sx={{ color: "text.secondary", mr: -0.5 }}><CloseIcon sx={{ fontSize: 20 }} /></IconButton>
        </Box>
        <Box sx={{ px: 2.5, py: 2, display: "flex", flexDirection: "column", gap: 2.5, overflowY: "auto" }}>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>View</Typography>
            <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small" fullWidth>
              <ToggleButton value="list" sx={{ flex: 1 }}><ListIcon sx={{ fontSize: 14, mr: 0.5 }} />List</ToggleButton>
              <ToggleButton value="map" sx={{ flex: 1 }}><MapIcon sx={{ fontSize: 14, mr: 0.5 }} />Map</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Divider />
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>TTC Subway</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {[{ id: "all", label: "All Lines", color: null }].concat(TTC_LINES).map(line => {
                const count = line.id === "all" ? alerts.length : alerts.filter(a => a.route === line.id).length;
                const active = lineFilter === line.id;
                return (
                  <Box key={line.id} onClick={() => { setLineFilter(line.id); setFilterDrawerOpen(false); }} sx={{
                    display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.75, borderRadius: 1, cursor: "pointer",
                    bgcolor: active ? (dark ? "#1e293b" : "#f1f5f9") : "transparent",
                    border: `1px solid ${active ? (line.color || "#3b82f6") : "transparent"}`,
                  }}>
                    {line.color && <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: line.color, flexShrink: 0 }} />}
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: active ? 700 : 400, color: active ? "text.primary" : "text.secondary", flex: 1 }}>
                      {line.id === "all" ? "All Lines" : `${line.label} · ${line.name}`}
                    </Typography>
                    <Chip label={count} size="small" sx={{ height: 18, fontSize: "0.65rem", bgcolor: dark ? "#0f172a" : "#e2e8f0", color: "text.secondary" }} />
                  </Box>
                );
              })}
            </Box>

            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mt: 2, mb: 1 }}>GO Train</Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {[
                { id: "LW", label: "Lakeshore West", color: "#98002e" },
                { id: "LE", label: "Lakeshore East", color: "#ff0d00" },
              ].map(line => {
                const goLine = goLines.find(l => l.code === line.id);
                const hasIssue = goLine && goLine.updates > 0;
                return (
                  <Box key={line.id} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.75, borderRadius: 1, bgcolor: dark ? "#0f172a" : "#f8fafc" }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: line.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", flex: 1 }}>{line.label}</Typography>
                    {hasIssue
                      ? <Chip label={`${goLine.updates} update${goLine.updates !== 1 ? "s" : ""}`} size="small" sx={{ height: 16, fontSize: "0.62rem", fontWeight: 700, bgcolor: dark ? "#431407" : "#fff7ed", color: "#fb923c" }} />
                      : <Typography sx={{ fontSize: "0.7rem", color: "#10b981", fontWeight: 600 }}>Good</Typography>}
                  </Box>
                );
              })}
            </Box>

            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mt: 2, mb: 1 }}>UP Express</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.75, borderRadius: 1, bgcolor: dark ? "#0f172a" : "#f8fafc" }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#0072BB", flexShrink: 0 }} />
              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", flex: 1 }}>Union Pearson Express</Typography>
              {upAlerts.length > 0
                ? <Chip label={`${upAlerts.length} update${upAlerts.length !== 1 ? "s" : ""}`} size="small" sx={{ height: 16, fontSize: "0.62rem", fontWeight: 700, bgcolor: dark ? "#431407" : "#fff7ed", color: "#fb923c" }} />
                : <Typography sx={{ fontSize: "0.7rem", color: "#10b981", fontWeight: 600 }}>Good</Typography>}
            </Box>
          </Box>
          <Divider />
          <Box>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", mb: 1 }}>Appearance</Typography>
            <ToggleButtonGroup value={colorMode} exclusive onChange={(_, v) => v && setColorMode(v)} size="small" fullWidth>
              <ToggleButton value="light" sx={{ flex: 1 }}>Light</ToggleButton>
              <ToggleButton value="dark" sx={{ flex: 1 }}>Dark</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
      </SwipeableDrawer>

      {error && <Alert severity="error" sx={{ mx: 2, mt: 1, fontSize: "0.82rem" }}>{error}</Alert>}

      {/* Content — map replaces list entirely, consistent with other pages */}
      {viewMode === "map" ? (
        <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <CircularProgress size={30} sx={{ color: "#FFCD00" }} />
            </Box>
          ) : (
            <SubwayMap alerts={displayed} colorMode={colorMode} border={border} lineFilter={lineFilter} goLines={goLines} upAlerts={upAlerts} />
          )}
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {!loading && !error && <TTCStatusSection alerts={alerts} dark={dark} border={border} />}
          <GOStatusSection dark={dark} border={border} />
          <UPStatusSection dark={dark} border={border} />
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ height: 52, bgcolor: barBg, borderTop: `1px solid ${border}`, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>Data: alerts.ttc.ca · Metrolinx</Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981", animation: "pulse 2s infinite" }} />
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>Live · 60s</Typography>
        </Box>
      </Box>
    </Box>
  );
}
